import { useState, useEffect } from 'react';
import { 
  Briefcase, MapPin, Clock, DollarSign, Users, 
  GraduationCap, CheckCircle, Search, Sparkles, 
  ArrowRight, Building2, Target, Award, Heart, BriefcaseBusiness
} from 'lucide-react';
import { ref, onValue } from "firebase/database";
import { db } from "./firebase"; 
import Navbar from './Navbar';

export default function CareerPage() {
  const [selectedJob, setSelectedJob] = useState(null);
  const [filter, setFilter] = useState('all');
  const [jobListings, setJobListings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [likedJobs, setLikedJobs] = useState([]);

  // ✅ Fetch jobs from Firebase
  useEffect(() => {
    const jobsRef = ref(db, "jobs");
    onValue(jobsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const jobArray = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setJobListings(jobArray);
      } else {
        setJobListings([]);
      }
    });
  }, []);

  // ✅ Like toggle
  const toggleLike = (jobId) => {
    setLikedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  // ✅ Search + Filter
  const filteredJobs = jobListings.filter(job => {
    const matchesFilter = filter === 'all' || job.department?.toLowerCase() === filter.toLowerCase();
    const matchesSearch = job.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         job.company?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // ✅ Categories
  const categories = [
    { id: 'all', label: 'All Jobs', icon: Briefcase, color: 'from-blue-500 to-cyan-500' },
    { id: 'engineering', label: 'Job offer', icon: Target, color: 'from-purple-500 to-pink-500' },
    { id: 'design', label: ' Internship', icon: Sparkles, color: 'from-orange-500 to-red-500' },
    { id: 'project management', label: 'Unpaid Internship', icon: Award, color: 'from-green-500 to-emerald-500' }
  ];

  return (
    <>
      <Navbar />

      <div className="pt-20 min-h-screen bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950">
        
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 animate-pulse"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(168,85,247,0.1),transparent_50%)]"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
            <div className="text-center space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full border border-white/20 backdrop-blur-sm">
                <Sparkles className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-white/90 font-medium">{jobListings.length} Openings</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
               Freshers Job Portal
              </h1>
              
              <p className="text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
                Discover exciting opportunities tailored for fresh graduates 🚀
              </p>

              {/* Search */}
              <div className="max-w-2xl mx-auto mt-8">
                <div className="relative group">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input
                    type="text"
                    placeholder="Search jobs, companies, or locations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:border-blue-400/50"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = filter === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setFilter(cat.id)}
                  className={`group relative overflow-hidden p-6 rounded-2xl backdrop-blur-sm transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-br ' + cat.color + ' shadow-2xl scale-105'
                      : 'bg-white/5 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="p-3 rounded-xl bg-white/10 group-hover:scale-110">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="font-bold text-sm text-white">{cat.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 pb-12 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-6">
            
            {/* Jobs List */}
            <div className="lg:col-span-2 space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-white/20">
              {filteredJobs.map((job, index) => {
                const isSelected = selectedJob?.id === job.id;
                const isLiked = likedJobs.includes(job.id);
                return (
                  <div
                    key={job.id}
                    onClick={() => setSelectedJob(job)}
                    className={`group relative p-6 rounded-2xl cursor-pointer border-2 transition-all duration-500 ${
                      isSelected ? 'border-blue-400 shadow-2xl scale-[1.02]' : 'border-white/10 hover:border-white/30 hover:scale-[1.01]'
                    } bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md`}
                    style={{
                      animation: `slideIn 0.5s ease-out ${index * 0.1}s both`
                    }}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className="text-xs text-blue-400 font-semibold">{job.company}</span>
                        <h3 className="text-xl font-bold text-white">{job.title}</h3>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleLike(job.id); }}
                        className="p-2 rounded-full bg-white/10 hover:bg-white/20"
                      >
                        <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-500 text-red-500' : 'text-white/60'}`} />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm text-white/70">
                      <span><MapPin className="w-4 h-4 inline text-green-400" /> {job.city}</span>
                      <span><Clock className="w-4 h-4 inline text-orange-400" /> {job.workMode || "On-site"}</span>
                      <span><BriefcaseBusiness className="w-4 h-4 inline text-purple-400" /> {job.jobType || "Full-time"}</span>
                    </div>

                    <div className="flex justify-between items-center mt-4">
                      <span className="text-emerald-400 font-semibold"><DollarSign className="w-4 h-4 inline" /> {job.package ? `₹${job.package}` : "₹As per company norms"}</span>
                      <span className="text-green-400"><Users className="w-4 h-4 inline" /> {job.openings || "Multiple"} slots</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Job Details */}
            <div className="lg:col-span-3">
              {selectedJob ? (
                <div className="sticky top-24 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
                  <div className="mb-6">
                    <h2 className="text-3xl font-black text-white">{selectedJob.title}</h2>
                    <p className="text-white/70">{selectedJob.company}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 p-4 rounded-2xl">
                      <MapPin className="w-5 h-5 text-blue-400 mb-1" />
                      <p className="text-white font-semibold">{selectedJob.city}</p>
                      <p className="text-xs text-white/50">{selectedJob.address}</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 p-4 rounded-2xl">
                      <Clock className="w-5 h-5 text-purple-400 mb-1" />
                      <p className="text-white font-semibold">{selectedJob.workMode}</p>
                    </div>
                    <div className="bg-gradient-to-br from-pink-500/20 to-pink-600/10 p-4 rounded-2xl">
                      <BriefcaseBusiness className="w-5 h-5 text-pink-400 mb-1" />
                      <p className="text-white font-semibold">{selectedJob.jobType || "Full-time"}</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 p-4 rounded-2xl">
                      <DollarSign className="w-5 h-5 text-green-400 mb-1" />
                      <p className="text-white font-semibold">₹{selectedJob.package}</p>
                    </div>
                    <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 p-4 rounded-2xl">
                      <GraduationCap className="w-5 h-5 text-orange-400 mb-1" />
                      <p className="text-white font-semibold">{selectedJob.academics}</p>
                      <p className="text-xs text-white/50">{selectedJob.academicType}</p>
                    </div>
                  </div>

                  <div className="bg-white/5 p-6 rounded-2xl mb-6">
                    <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      Job Details
                    </h3>
                    <p className="text-white/80 whitespace-pre-line">{selectedJob.details}</p>
                  </div>

                  <a
                    href={selectedJob.applyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-bold py-4 rounded-2xl text-center shadow-lg hover:scale-105 transition"
                  >
                    Apply Now →
                  </a>
                </div>
              ) : (
                <div className="sticky top-24 bg-white/5 rounded-3xl p-16 text-center">
                  <Briefcase className="w-20 h-20 text-white/30 mx-auto" />
                  <h3 className="text-3xl font-black text-white mt-6">Pick Your Next Adventure</h3>
                  <p className="text-white/60">Select a job to explore full details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .scrollbar-thin::-webkit-scrollbar { width: 6px; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 10px; }
      `}</style>
    </>
  );
}
